/*
 * kb-cart-upsell.js — v9.9.19
 * ===========================================================================
 * Kennedy Blue post-add-to-cart upsell. Replaces the Zipify OneClickUpsell
 * pre-purchase popup.
 *
 * DESIGN PRINCIPLE, and the reason this file exists:
 *   We NEVER intercept the add-to-cart click.
 *
 * Zipify hooked the button by selector (customAddToCartButton = '.ajax-add-to-cart')
 * and held the product out of the cart while its popup was open. On a decline
 * the product could be lost silently — measured live on 12SEP2026 on Athena:
 * cart read {Kylie} with the popup open, and still {Kylie} after "Decline this
 * offer". A $139.99 dress dropped to protect a $7.99 bag.
 *
 * Instead this file OBSERVES the network. It wraps XMLHttpRequest and fetch,
 * watches for a successful POST to /cart/add(.js), and only then renders an
 * offer. The theme's own handlers run completely untouched. If this file
 * throws, errors, or is deleted, add-to-cart behaves exactly as it does today.
 *
 * That also solves a second problem: products-custom.js has FOUR separate
 * add-to-cart paths (the normal product form ~line 1420 and ~1494, addCart()
 * at ~1688 used by the old recommendations popup, the '.cart-add' drawer
 * handler at ~1592, and the AHTO/try-on submit which calls window.kbCartInit).
 * Hooking handlers would catch some and miss others. The network layer catches
 * all of them, plus Globo Product Options and Easy Bundle Builder.
 *
 * COLLECTION MATCHING
 *   /cart.js line items expose product_type and handle but NOT collections.
 *   So the Liquid section publishes window.KBUpsellCtx on every page with the
 *   current product's collection handles (product pages) or the current
 *   collection handle (collection pages). Rules matching on a collection use
 *   that context. Product-type and handle rules need no context and work
 *   everywhere, including quick-add. See ctxCollections().
 *
 * CONSENT
 *   Nothing is written to a cookie or localStorage. Session-scoped UX state
 *   only. dataLayer pushes are gated on Shopify's Customer Privacy analytics
 *   signal, matching kb-styling-concierge.js.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- config */

  var CFG = null;
  try {
    var el = document.getElementById('kbup-config');
    if (el) CFG = JSON.parse(el.textContent || el.innerText || '{}');
  } catch (e) { CFG = null; }
  if (!CFG || !CFG.settings || !CFG.settings.enabled) return;

  var S = CFG.settings;

  /* ------------------------------------------------- v9.9.17: page excludes
   *
   * Some pages must never show an upsell, whatever is added on them —
   * giveaway landing pages, wholesale, anything running a paid campaign where
   * a popup would wreck the measurement.
   *
   * Two lists, because "exclude this one page" and "exclude everything under
   * this path" are different jobs and guessing which one a merchant meant from
   * a single field is how you get surprises:
   *
   *   Exact     /pages/giveaways        only that page
   *   Contains  /pages/wholesale        that page AND anything beneath it
   *
   * Checked ONCE, here, before a single hook is installed. An excluded page
   * therefore behaves exactly like the theme without this feature: no
   * listeners, no drawer hold, no cost.
   */
  function kbNormPath(p) {
    p = String(p == null ? '' : p).trim().toLowerCase();
    if (!p) return '';
    // a merchant will paste a full URL as often as a path; keep only the path
    var m = p.match(/^https?:\/\/[^\/]+(\/.*)?$/);
    if (m) p = m[1] || '/';
    if (p.charAt(0) !== '/') p = '/' + p;
    if (p.length > 1 && p.charAt(p.length - 1) === '/') p = p.slice(0, -1);
    return p;
  }
  function kbList(v) {
    if (!v) return [];
    return (v instanceof Array ? v : String(v).split(/[\r\n,]+/))
      .map(function (x) { return String(x).trim(); })
      .filter(Boolean);
  }

  var EXCLUDE_EXACT = kbList(S.excludeExact).map(kbNormPath);
  var EXCLUDE_CONTAINS = kbList(S.excludeContains).map(function (x) {
    return String(x).trim().toLowerCase();
  });

  function pageExcluded() {
    var path = kbNormPath(location.pathname);
    // search is included for "contains" so ?view= and ?variant= can be excluded
    var full = path + String(location.search || '').toLowerCase();
    var i;
    for (i = 0; i < EXCLUDE_EXACT.length; i++) {
      if (EXCLUDE_EXACT[i] && path === EXCLUDE_EXACT[i]) {
        return 'exact match on "' + EXCLUDE_EXACT[i] + '"';
      }
    }
    for (i = 0; i < EXCLUDE_CONTAINS.length; i++) {
      if (EXCLUDE_CONTAINS[i] && full.indexOf(EXCLUDE_CONTAINS[i]) !== -1) {
        return 'contains "' + EXCLUDE_CONTAINS[i] + '"';
      }
    }
    return '';
  }

  var kbExcludedBy = pageExcluded();
  if (kbExcludedBy) {
    if (S.debug && window.console && window.console.log) {
      console.log('[kb-upsell] this page is excluded (' + kbExcludedBy +
                  ') — not installing anything');
    }
    return;
  }

  var OFFERS = (CFG.offers || []).filter(function (o) {
    return o && o.active && o.offer && o.offer.variants && o.offer.variants.length;
  });
  if (!OFFERS.length) return;

  /* v9.9.12 — a duplicate reference name is a silent double failure: the key is
   * both the once-per-session dedupe identity and the `_upsell_offer` value
   * written on the order line. Two blocks sharing one means the second offer
   * never shows after the first, and the revenue for both lands under one
   * name. Make it loud in the console and keep them distinct. */
  (function () {
    var seenKeys = {};
    for (var i = 0; i < OFFERS.length; i++) {
      var k = OFFERS[i].key;
      if (!seenKeys[k]) { seenKeys[k] = 1; continue; }
      seenKeys[k]++;
      OFFERS[i].key = k + '-' + seenKeys[k];
      if (window.console && window.console.warn) {
        console.warn('[kb-upsell] two offers share the reference name "' + k +
                     '". Renamed the second to "' + OFFERS[i].key +
                     '" so it can still show and be counted. Give them distinct names.');
      }
    }
  })();

  var SESSION_KEY = 'kb_upsell_state';
  var MAX_CHAIN = Math.max(1, Math.min(5, parseInt(S.maxChain, 10) || 3));

  /* ------------------------------------------------------------- utilities */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(cents) {
    var n = (parseInt(cents, 10) || 0) / 100;
    return '$' + n.toFixed(2);
  }
  function norm(s) {
    return String(s == null ? '' : s).trim().toLowerCase();
  }
  function log() {
    if (S.debug && window.console) console.log.apply(console, ['[kb-upsell]'].concat([].slice.call(arguments)));
  }

  /* --------------------------------------------------------------- consent */

  function mayTrack() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
      try { return !!cp.analyticsProcessingAllowed(); } catch (e) {}
    }
    // Privacy API not loaded yet: treat as DENIED so we never beat the banner.
    // v9.9.12: this read `return !window.Shopify ? true : false`, i.e. it
    // ALLOWED tracking when Shopify was absent — the opposite of the line
    // above. Dead on a real storefront, live in previews and embeds.
    return false;
  }
  function track(event, params) {
    if (!mayTrack()) return;
    window.dataLayer = window.dataLayer || [];
    var o = { event: event };
    for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) o[k] = params[k];
    window.dataLayer.push(o);
    log('track', event, o);
  }

  /* ------------------------------------------------------- session memory */

  function readState() {
    try {
      var raw = window.sessionStorage.getItem(SESSION_KEY);
      var o = raw ? JSON.parse(raw) : null;
      if (o && typeof o === 'object') {
        o.seen = o.seen || {};
        o.expired = o.expired || {};
        return o;
      }
    } catch (e) {}
    return { seen: {}, expired: {} };
  }
  function writeState(st) {
    try { window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(st)); } catch (e) {}
  }
  function markSeen(key) { var st = readState(); st.seen[key] = 1; writeState(st); }
  function markExpired(key) { var st = readState(); st.expired[key] = 1; writeState(st); }
  function wasSeen(key) { var st = readState(); return !!(st.seen[key] || st.expired[key]); }

  /* ------------------------------------------------------- page context */

  function ctxCollections() {
    var c = window.KBUpsellCtx || {};
    var out = [];
    if (c.collections && c.collections.length) out = out.concat(c.collections);
    if (c.collection) out.push(c.collection);
    return out.map(norm);
  }
  function ctxHandle() { return norm((window.KBUpsellCtx || {}).handle); }

  /* Could ANY offer still fire for the thing this page adds?
   *
   * This exists purely to protect the drawer. Holding the drawer shut costs
   * nothing when an offer follows, but on an add with no offer the customer
   * waits with no feedback until we release. Measured on the 16SEP2026
   * preview before this check existed:
   *     second dress (offer already seen)      1781ms with no drawer
   *     Home Try-On sample                     1315ms with no drawer
   * Both are worse than the flash we set out to remove.
   *
   * On a product page KBUpsellCtx tells us the handle, type and collections of
   * the product being viewed, which is exactly what ruleMatches needs — so we
   * can answer this before the POST even leaves. Anywhere else (collection
   * quick-add, cart drawer re-add) we cannot know what is being added, so we
   * stay safe and hold.
   */
  function pageMightOffer() {
    var c = window.KBUpsellCtx || {};
    if (!c.handle) return true;                    // not a PDP — cannot know
    var pseudo = { handle: c.handle, product_type: c.type };
    for (var i = 0; i < OFFERS.length; i++) {
      if (wasSeen(OFFERS[i].key)) continue;
      if (ruleMatches(OFFERS[i], pseudo)) return true;
    }
    return false;
  }

  /* Same question, but now we know exactly what was added. No cart fetch —
   * this is the cheap gate that lets us hand the drawer back immediately on
   * an add that will never produce an offer. */
  function anyRuleCouldMatch(item) {
    for (var i = 0; i < OFFERS.length; i++) {
      if (wasSeen(OFFERS[i].key)) continue;
      if (ruleMatches(OFFERS[i], item)) return true;
    }
    return false;
  }

  /* --------------------------------------------------------- rule matching */

  // item is a /cart.js line item (or the add.js response, same shape).
  function ruleMatches(rule, item) {
    var t = rule.trigger || {};
    var vals = (t.values || []).map(norm).filter(Boolean);
    if (!vals.length) return false;

    if (t.type === 'type') {
      return vals.indexOf(norm(item.product_type)) !== -1;
    }
    if (t.type === 'handles') {
      return vals.indexOf(norm(item.handle)) !== -1;
    }
    // collection
    var mine = ctxCollections();
    if (!mine.length) return false;
    // Only trust page context when the context is actually about this product.
    // On a PDP, KBUpsellCtx.handle is the product being viewed. On a collection
    // page there is no handle, and the collection itself is the evidence.
    var h = ctxHandle();
    if (h && h !== norm(item.handle)) return false;
    for (var i = 0; i < vals.length; i++) {
      if (mine.indexOf(vals[i]) !== -1) return true;
    }
    return false;
  }

  function cartHasProduct(cart, productId) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].product_id) === String(productId)) return true;
    }
    return false;
  }

  function availableVariants(offer) {
    return (offer.variants || []).filter(function (v) { return v.available; });
  }

  /* Fill the tokens in a merchant-editable string.
   *
   *   {n}   the number
   *   {es}  "es" unless n is 1        swatch  -> swatches
   *   {s}   "s"  unless n is 1        colour  -> colours
   *
   * Both plural tokens exist because English does not have one rule. The
   * first draft only had {s} and produced "Add 3 free swatchs" — a typo in
   * the most prominent button on the offer. {es} must be replaced BEFORE {s},
   * or {s} eats the s in {es} and leaves a stray "e".
   */
  function fill(tpl, n) {
    return String(tpl == null ? '' : tpl)
      .replace(/\{n\}/g, n)
      .replace(/\{es\}/g, n === 1 ? '' : 'es')
      .replace(/\{s\}/g, n === 1 ? '' : 's');
  }

  /* ------------------------------------------- v9.9.19: the free allowance
   *
   * The thing that makes swatches free is a Shopify AUTOMATIC DISCOUNT —
   * "Free Swatches", a flat $6.00 off, spread across the five swatch products.
   * Verified against the Admin API on 18SEP2026: one active automatic discount,
   * DiscountAmount 6.0, appliesOnEachItem false, no minimum.
   *
   * $6.00 is 3 x $2.00, which is where "3 free" comes from. But it is a
   * CART-WIDE CREDIT, not three free items per popup. So if she already has
   * three swatches in the cart and this popup cheerfully adds three more, the
   * cart is $12.00, the discount is still $6.00, and she is charged $6.00 after
   * reading the word "free".
   *
   * Hence: count what is already in the cart, offer only what is genuinely
   * still free, and show nothing at all when the allowance is gone. A popup
   * that promises free and then charges is worse than no popup.
   */
  function cartUnitsIn(cart, handles) {
    var items = (cart && cart.items) || [], n = 0, i;
    if (!handles.length) return 0;
    for (i = 0; i < items.length; i++) {
      if (handles.indexOf(norm(items[i].handle)) !== -1) n += (items[i].quantity || 0);
    }
    return n;
  }

  function freeAllowance(rule, cart) {
    var cap = parseInt(rule.pickCount, 10);
    if (!cap || cap < 1) cap = 3;
    var pool = kbList(rule.freePool).map(norm).filter(Boolean);
    // No pool configured means we cannot know what is already free. Offer the
    // full count rather than silently suppressing the offer forever.
    if (!pool.length) return cap;
    return Math.max(0, cap - cartUnitsIn(cart, pool));
  }



  /* Swap a thumbnail's photo. The element may have started life as a <span>
   * placeholder (no photo on the default variant), so upgrade it to an <img>
   * rather than silently doing nothing. */
  function setThumb(el, src, cls, attr, attrVal) {
    if (!el) return;
    if (!src) return;
    if (el.tagName === 'IMG') { el.setAttribute('src', src); return; }
    var img = document.createElement('img');
    img.className = cls;
    img.setAttribute('src', src);
    img.setAttribute('alt', '');
    img.setAttribute('loading', 'lazy');
    if (attr === 'id') img.id = attrVal; else img.setAttribute(attr, attrVal);
    el.parentNode.replaceChild(img, el);
  }
  /* -------------------------------------------- the colour dropdown (v9.9.10)
   *
   * Jimmy, looking at 67 text buttons: "dropdown, not the buttons. Cant tell
   * what color is what."
   *
   * Every colour variant on this store carries its own photo, so each row
   * shows a real swatch. Not a native <select> — that cannot render an image —
   * but it behaves like one: a closed button showing the current choice, a
   * panel that opens, a search box, Escape to close.
   *
   * `rows` are { key, label, image }. `key` is what comes back on pick.
   */
  function ddHtml(id, rows, currentKey, placeholder) {
    var cur = null, i;
    for (i = 0; i < rows.length; i++) if (rows[i].key === currentKey) { cur = rows[i]; break; }
    if (!cur) cur = rows[0] || { key: '', label: '', image: '' };
    return '' +
      '<div class="kbup__dd" id="' + id + '">' +
        '<button type="button" class="kbup__ddbtn" id="' + id + '-btn" ' +
                'aria-haspopup="listbox" aria-expanded="false">' +
          swatchHtml(cur.image) +
          '<span class="kbup__ddname" id="' + id + '-name">' + esc(cur.label) + '</span>' +
        '</button>' +
        '<div class="kbup__ddpanel" id="' + id + '-panel" role="listbox">' +
          (rows.length > 8
            ? '<input type="search" class="kbup__search" id="' + id + '-search" ' +
              'placeholder="' + esc(placeholder) + '" aria-label="' + esc(placeholder) + '">'
            : '') +
          '<div class="kbup__ddopts" id="' + id + '-opts">' +
            rows.map(function (r) {
              return '<button type="button" class="kbup__ddopt" role="option" ' +
                     'data-ddkey="' + esc(r.key) + '" data-ddlabel="' + esc(r.label) + '" ' +
                     'aria-selected="' + (r.key === cur.key ? 'true' : 'false') + '">' +
                     swatchHtml(r.image) + '<span>' + esc(r.label) + '</span></button>';
            }).join('') +
          '</div>' +
          '<div class="kbup__ddempty" id="' + id + '-empty" hidden>No match</div>' +
        '</div>' +
      '</div>';
  }
  function swatchHtml(src) {
    return src
      ? '<img class="kbup__sw" src="' + esc(src) + '" alt="" loading="lazy">'
      : '<span class="kbup__sw"></span>';
  }
  // Wire one dropdown. onPick gets (key, label, image).
  function ddWire(root, id, onPick) {
    var wrap = root.querySelector('#' + id);
    if (!wrap) return;
    var btn = root.querySelector('#' + id + '-btn');
    var panel = root.querySelector('#' + id + '-panel');
    var search = root.querySelector('#' + id + '-search');
    var empty = root.querySelector('#' + id + '-empty');
    function close() { panel.classList.remove('kbup__ddpanel--open'); btn.setAttribute('aria-expanded', 'false'); }
    function open() {
      panel.classList.add('kbup__ddpanel--open');
      btn.setAttribute('aria-expanded', 'true');
      if (search) { search.value = ''; search.dispatchEvent(new Event('input')); search.focus(); }
    }
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (panel.classList.contains('kbup__ddpanel--open')) close(); else open();
    });
    // click anywhere else closes it, like a real select
    root.addEventListener('click', function (e) { if (!wrap.contains(e.target)) close(); });
    if (search) {
      search.addEventListener('click', function (e) { e.stopPropagation(); });
      search.addEventListener('input', function () {
        var q = norm(this.value), shown = 0;
        [].forEach.call(wrap.querySelectorAll('.kbup__ddopt'), function (o) {
          var hit = !q || norm(o.getAttribute('data-ddlabel')).indexOf(q) !== -1;
          o.style.display = hit ? '' : 'none';
          if (hit) shown++;
        });
        if (empty) empty.hidden = shown > 0;
      });
    }
    [].forEach.call(wrap.querySelectorAll('.kbup__ddopt'), function (o) {
      o.addEventListener('click', function (e) {
        e.stopPropagation();
        [].forEach.call(wrap.querySelectorAll('.kbup__ddopt'), function (x) { x.setAttribute('aria-selected', 'false'); });
        o.setAttribute('aria-selected', 'true');
        var label = o.getAttribute('data-ddlabel');
        var img = o.querySelector('.kbup__sw');
        var nameEl = root.querySelector('#' + id + '-name');
        if (nameEl) nameEl.textContent = label;
        var btnSw = btn.querySelector('.kbup__sw');
        if (btnSw && img) {
          var src = img.getAttribute('src');
          if (src && btnSw.tagName === 'IMG') btnSw.setAttribute('src', src);
        }
        close();
        onPick(o.getAttribute('data-ddkey'), label);
      });
    });
    return { close: close };
  }

  /* ------------------------------------ v9.9.19: the multi-pick dropdown
   *
   * Same markup and therefore the same CSS as the single-select one above —
   * deliberately. It is the control Jimmy already signed off on for 67 colours
   * ("dropdown, not the buttons"), and the swatch list is 76. The only
   * differences are a tick on the chosen rows and a cap: once she has picked
   * her allowance the remaining rows go disabled rather than silently
   * swallowing the click, so the limit is visible instead of mysterious.
   */
  function ddMultiHtml(id, rows, placeholder, summaryText) {
    return '' +
      '<div class="kbup__dd kbup__dd--multi" id="' + id + '">' +
        '<button type="button" class="kbup__ddbtn" id="' + id + '-btn" ' +
                'aria-haspopup="listbox" aria-expanded="false">' +
          '<span class="kbup__ddname" id="' + id + '-name">' + esc(summaryText) + '</span>' +
        '</button>' +
        '<div class="kbup__ddpanel" id="' + id + '-panel" role="listbox" aria-multiselectable="true">' +
          (rows.length > 8
            ? '<input type="search" class="kbup__search" id="' + id + '-search" ' +
              'placeholder="' + esc(placeholder) + '" aria-label="' + esc(placeholder) + '">'
            : '') +
          '<div class="kbup__ddopts" id="' + id + '-opts">' +
            rows.map(function (r) {
              return '<button type="button" class="kbup__ddopt" role="option" ' +
                     'data-ddkey="' + esc(r.key) + '" data-ddlabel="' + esc(r.label) + '" ' +
                     'aria-selected="false">' +
                     swatchHtml(r.image) + '<span>' + esc(r.label) + '</span>' +
                     '<span class="kbup__ddtick" aria-hidden="true">&#10003;</span></button>';
            }).join('') +
          '</div>' +
          '<div class="kbup__ddempty" id="' + id + '-empty" hidden>No match</div>' +
        '</div>' +
      '</div>';
  }

  // onToggle gets (key, label, image). The CALLER owns the selection list and
  // the cap; this only reports the tap and paints what it is told to paint.
  function ddMultiWire(root, id, onToggle) {
    var wrap = root.querySelector('#' + id);
    if (!wrap) return null;
    var btn = root.querySelector('#' + id + '-btn');
    var panel = root.querySelector('#' + id + '-panel');
    var search = root.querySelector('#' + id + '-search');
    var empty = root.querySelector('#' + id + '-empty');

    function close() { panel.classList.remove('kbup__ddpanel--open'); btn.setAttribute('aria-expanded', 'false'); }
    function open() {
      panel.classList.add('kbup__ddpanel--open');
      btn.setAttribute('aria-expanded', 'true');
      if (search) { search.value = ''; search.dispatchEvent(new Event('input')); search.focus(); }
    }
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (panel.classList.contains('kbup__ddpanel--open')) close(); else open();
    });
    root.addEventListener('click', function (e) { if (!wrap.contains(e.target)) close(); });

    if (search) {
      search.addEventListener('click', function (e) { e.stopPropagation(); });
      search.addEventListener('input', function () {
        var q = norm(this.value), shown = 0;
        [].forEach.call(wrap.querySelectorAll('.kbup__ddopt'), function (o) {
          var hit = !q || norm(o.getAttribute('data-ddlabel')).indexOf(q) !== -1;
          o.style.display = hit ? '' : 'none';
          if (hit) shown++;
        });
        if (empty) empty.hidden = shown > 0;
      });
    }

    [].forEach.call(wrap.querySelectorAll('.kbup__ddopt'), function (o) {
      o.addEventListener('click', function (e) {
        e.stopPropagation();
        // The panel stays OPEN on a pick. She is choosing three; closing after
        // each one would mean three extra taps to reopen it.
        var img = o.querySelector('.kbup__sw');
        onToggle(o.getAttribute('data-ddkey'), o.getAttribute('data-ddlabel'),
                 (img && img.tagName === 'IMG') ? img.getAttribute('src') : '');
      });
    });

    function optFor(key) {
      var out = null;
      [].forEach.call(wrap.querySelectorAll('.kbup__ddopt'), function (o) {
        if (o.getAttribute('data-ddkey') === String(key)) out = o;
      });
      return out;
    }

    return {
      close: close,
      setName: function (t) {
        var n = root.querySelector('#' + id + '-name');
        if (n) n.textContent = t;
      },
      mark: function (key, on) {
        var o = optFor(key);
        if (!o) return;
        o.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) o.classList.add('kbup__ddopt--on'); else o.classList.remove('kbup__ddopt--on');
      },
      // At the cap, everything she has NOT picked goes disabled. Her own picks
      // stay live so she can always change her mind without hunting for a reset.
      lock: function (locked) {
        [].forEach.call(wrap.querySelectorAll('.kbup__ddopt'), function (o) {
          var dis = !!locked && !o.classList.contains('kbup__ddopt--on');
          o.disabled = dis;
          if (dis) o.classList.add('kbup__ddopt--off'); else o.classList.remove('kbup__ddopt--off');
        });
      }
    };
  }

  /* ----------------------------------------------------- colour matching */

  // Exact variant-title match first. pocket-square and coordinating-solid-ties
  // each carry 67 variants using the same colour names as the dresses, so the
  // exact match is the common case. floral-pocket-square only has 6 (Dark
  // Green, Black, Blue, Light Purple, Pink, Brown), which is what the
  // per-offer family map is for.
  function pickVariant(rule, offer, item) {
    var avail = availableVariants(offer);
    if (!avail.length) return null;
    if (!rule.matchVariant) return { variant: avail[0], matched: false };

    var wants = colourCandidates(item).map(norm).filter(Boolean);
    if (!wants.length) return { variant: avail[0], matched: false };

    var i, j;
    // exact title match on any candidate
    for (j = 0; j < wants.length; j++) {
      for (i = 0; i < avail.length; i++) {
        if (norm(avail[i].title) === wants[j]) return { variant: avail[i], matched: true };
      }
    }
    // then the per-offer family map, same order
    var map = rule.familyMap || {};
    for (j = 0; j < wants.length; j++) {
      var fam = map[wants[j]];
      if (!fam) continue;
      var f = norm(fam);
      for (i = 0; i < avail.length; i++) {
        if (norm(avail[i].title) === f) return { variant: avail[i], matched: true };
      }
    }
    return { variant: avail[0], matched: false };
  }

  /* ------------------------------------------------- add-on colour (v9.9.9) */

  // An add-on's selectable variants. Liquid only emits `variants` when the
  // product has more than one, so a planner or a book returns [] and keeps
  // its single fixed variantId.
  function aoVariants(a) {
    var out = [];
    var vs = (a && a.variants) || [];
    for (var i = 0; i < vs.length; i++) if (vs[i] && vs[i].available) out.push(vs[i]);
    return out;
  }

  // The colours offered by the shared picker: the union across every add-on
  // that has a choice, in first-seen order. Union rather than intersection —
  // if the tie carries Bordeaux and the pocket square does not, she should
  // still be able to pick Bordeaux and have the square fall back.
  function aoColourRows(addons) {
    var seen = {}, out = [];
    for (var i = 0; i < addons.length; i++) {
      var vs = aoVariants(addons[i]);
      for (var j = 0; j < vs.length; j++) {
        var t = vs[j].title;
        if (!t || t === 'Default Title') continue;
        var k = norm(t);
        if (seen[k]) continue;
        seen[k] = 1;
        out.push({ key: t, label: t, image: vs[j].image || '' });
      }
    }
    return out;
  }

  // Resolve one add-on against the shared colour. Exact title first, then the
  // offer's own family map, then that add-on's first available variant — the
  // same ladder pickVariant uses for the main product, so the two behave alike.
  function resolveAo(a, colour, rule) {
    var vs = aoVariants(a);
    // v9.9.11: carry the variant's OWN photo. Jimmy, on a Lavender pick still
    // showing the green tie: "Should change the images too." The row thumbnail
    // was the product's featured image and never moved.
    function hit(v, exact) {
      return { id: v.id, title: v.title, price: v.price, image: v.image || a.image || '', exact: exact };
    }
    if (!vs.length) {
      return { id: a.variantId, title: '', price: a.price, image: a.image || '', exact: true };
    }
    var want = norm(colour), i;
    for (i = 0; i < vs.length; i++) {
      if (norm(vs[i].title) === want) return hit(vs[i], true);
    }
    var fam = ((rule && rule.familyMap) || {})[want];
    if (fam) {
      var f = norm(fam);
      for (i = 0; i < vs.length; i++) {
        if (norm(vs[i].title) === f) return hit(vs[i], false);
      }
    }
    return hit(vs[0], false);
  }

  // What colour should the add-ons start on? Whatever she just added, if any
  // add-on carries it — otherwise the main offer's colour, otherwise the
  // first chip. Same intent as the main picker: never make her choose twice.
  function defaultAoColour(addons, item, mainVariant) {
    var chips = aoColourRows(addons);
    if (!chips.length) return '';
    var names = chips.map(function (c) { return c.label; });
    var lower = names.map(norm), i, j;
    var wants = colourCandidates(item || {}).map(norm).filter(Boolean);
    if (mainVariant && mainVariant.title) wants.push(norm(mainVariant.title));
    for (j = 0; j < wants.length; j++) {
      i = lower.indexOf(wants[j]);
      if (i !== -1) return names[i];
    }
    return names[0];
  }

  // Every candidate string that might be the colour, best guess first.
  //
  // options_with_values is the reliable source, but option ORDER is not
  // consistent across this catalogue — measured 16SEP2026:
  //   pocket-square / ties : one option, Color              -> "Moss"
  //   bridesmaid dresses   : Color first                    -> "Sky / 8"
  //   robes                : SIZE first, Color second       -> "Standard / Dusty Blue"
  // So the old fallback of variant_title.split(' / ')[0] read "Standard" off a
  // robe — a size, not a colour — and the match silently never landed. Rather
  // than guess the order, hand back every segment and let the caller try them
  // all against the offer's real variant titles.
  function colourCandidates(item) {
    var out = [];
    var owv = item.options_with_values || [];
    var i;
    for (i = 0; i < owv.length; i++) {
      if (/colou?r/i.test(owv[i].name || '') && owv[i].value) out.push(owv[i].value);
    }
    for (i = 0; i < owv.length; i++) {
      if (owv[i].value && out.indexOf(owv[i].value) === -1) out.push(owv[i].value);
    }
    var vt = item.variant_title || '';
    if (vt) {
      var parts = vt.split(' / ');
      for (i = 0; i < parts.length; i++) {
        var p = parts[i].trim();
        if (p && out.indexOf(p) === -1) out.push(p);
      }
    }
    return out;
  }

  /* ------------------------------------------------------------ cart calls */

  var ourAddInFlight = false;

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function addItems(items) {
    ourAddInFlight = true;
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'add failed'); });
      return r.json();
    }).then(function (res) {
      ourAddInFlight = false;
      return res;
    }, function (err) {
      ourAddInFlight = false;
      throw err;
    });
  }

  function refreshDrawer(open) {
    try {
      if (typeof window.kbCartInit === 'function') window.kbCartInit();
      if (open && typeof window.kbTriggerCartDrawer === 'function') window.kbTriggerCartDrawer(false);
    } catch (e) { log('drawer refresh failed', e); }
  }

  /* ------------------------------------------------------------- the modal */

  var root = null, timerHandle = null, state = null;
  // v9.9.16: paired with timerHandle — cleared in exactly the same two places.
  var wakeHandler = null;
  var drawerWatch = null, drawerWanted = false, holdTimer = null;

  /* The theme opens the cart drawer OPTIMISTICALLY — products-custom.js line
   * ~1581 calls cart_drawer_open(true) before the /cart/add.js POST has even
   * returned.
   *
   * v9.9.5 held the drawer from RENDER time, which was too late. Measured on
   * the 16SEP2026 preview (Kylie PDP, real add):
   *     +0ms    POST /cart/add.js sent
   *     +1ms    body.cart-drawer-open = true      <- theme, optimistic
   *     +767ms  POST returns
   *     +1822ms offer renders, drawer hidden
   * The drawer was visibly on screen for 1.8s first. That is the "flyout pops
   * out, then the pop-up" report.
   *
   * So the hold now starts the instant a /cart/add POST is SENT, from inside
   * the XHR/fetch wrappers — before the theme's own line runs. The observer
   * callback is a microtask, so the class is stripped in the same frame it is
   * added and nothing paints.
   *
   * Two safety rails, because suppressing the drawer forever is worse than a
   * flash:
   *   - drawerWanted records that the theme asked for the drawer, so if no
   *     offer materialises we put it back exactly as the theme intended.
   *   - HOLD_MS is a hard ceiling. If the offer has not rendered by then the
   *     drawer is handed back regardless. render() re-arms the hold itself, so
   *     a late offer (e.g. one deferred behind the consent banner) still works.
   */
  var HOLD_MS = 4000;

  function holdDrawer(armSafety) {
    if (document.body.classList.contains('cart-drawer-open')) drawerWanted = true;
    document.body.classList.remove('cart-drawer-open');
    if (!drawerWatch && window.MutationObserver) {
      drawerWatch = new MutationObserver(function () {
        if (document.body.classList.contains('cart-drawer-open')) {
          drawerWanted = true;
          document.body.classList.remove('cart-drawer-open');
        }
      });
      drawerWatch.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
    if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
    if (armSafety) {
      holdTimer = setTimeout(function () {
        holdTimer = null;
        if (!root) { log('no offer within ' + HOLD_MS + 'ms — handing the drawer back'); releaseDrawer(true); }
      }, HOLD_MS);
    }
  }

  /* restore=true  -> we are NOT showing an offer; put the drawer back if the
   *                  theme had asked for it.
   * restore=false -> the modal is taking over (or finish() is about to call
   *                  refreshDrawer, which opens it properly). */
  function releaseDrawer(restore) {
    // While the modal is on screen it owns the drawer. No error path, safety
    // timer or chained add may hand it back underneath the offer.
    if (restore && root) return;
    if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
    if (drawerWatch) { drawerWatch.disconnect(); drawerWatch = null; }
    if (restore && drawerWanted) document.body.classList.add('cart-drawer-open');
    drawerWanted = false;
  }

  function stopTimer() {
    if (timerHandle) { clearInterval(timerHandle); timerHandle = null; }
    if (wakeHandler) { document.removeEventListener('visibilitychange', wakeHandler); wakeHandler = null; }
  }

  function destroy() {
    stopTimer();
    releaseDrawer(false);
    if (root && root.parentNode) root.parentNode.removeChild(root);
    root = null;
    document.documentElement.classList.remove('kbup-locked');
    if (state && state.lastFocus && state.lastFocus.focus) {
      try { state.lastFocus.focus(); } catch (e) {}
    }
  }

  function finish(openDrawer) {
    destroy();
    refreshDrawer(openDrawer !== false);
  }

  function clockSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false">' +
           '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';
  }

  function render(queue, index, addedTitle, cart, addedItem) {
    /* v9.9.12 — kill the previous offer's countdown before drawing this one.
     *
     * The chain calls render() directly for offer 2 without going through
     * destroy(), so offer 1's setInterval survived. `root` by then holds
     * offer 2's markup, so when offer 1's clock hit zero it disabled
     * offer 2's Add button and relabelled it "Offer expired" — while
     * offer 2's own `expired` flag was still false. Repro: 5-minute timer,
     * accept offer 1 after 10 seconds, sit on offer 2 for five minutes. */
    stopTimer();

    if (index >= queue.length || index >= MAX_CHAIN) { finish(true); return; }

    var entry = queue[index];
    var rule = entry.rule, offer = entry.offer, pick = entry.pick;
    var variant = pick.variant;

    /* v9.9.12 — re-check against the cart as it is NOW, not as it was.
     *
     * The queue is built once in evaluate(), before any upsell add. Refetching
     * the cart for the next render was not enough on its own: render() only
     * used `cart` to filter the ADD-ONS, never to re-test the offer's own
     * product. So ticking the Pocket Square as an add-on on offer 1 and then
     * being shown the Pocket Square as offer 2 survived the refetch. Skip it
     * and move down the queue. Deliberately NOT markSeen'd — she has not
     * declined it, she already owns it. */
    /* v9.9.19 — a multi-pick offer is governed by the free ALLOWANCE, not by
     * "is this product in the cart". She may legitimately already own one
     * swatch and still have two free ones coming. The allowance check below
     * covers her; this one would wrongly suppress the whole offer. */
    var isPick = rule.layout === 'multiselect';

    if (!isPick && cart && cartHasProduct(cart, offer.productId)) {
      log('skipping ' + rule.key + ' — that product is already in her cart');
      render(queue, index + 1, addedTitle, cart, addedItem);
      return;
    }

    /* Re-read the allowance against the cart as it is NOW. The queue was built
     * before any upsell add, so an earlier offer in the chain may have spent
     * it. Deliberately NOT markSeen'd — she has not declined anything, there
     * is simply nothing free left to give her. */
    var allowance = 0;
    if (isPick) {
      allowance = freeAllowance(rule, cart);
      if (allowance <= 0) {
        log('skipping ' + rule.key + ' — no free allowance left in this cart');
        render(queue, index + 1, addedTitle, cart, addedItem);
        return;
      }
    }

    markSeen(rule.key);

    if (!root) {
      root = document.createElement('div');
      root.className = 'kbup kbup--t-' + (S.timerPosition || 'below');
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      root.setAttribute('aria-labelledby', 'kbup-head');
      document.body.appendChild(root);
      document.documentElement.classList.add('kbup-locked');
      holdDrawer(false);   // modal owns the hold now — no safety timeout
    }

    var showTimer = rule.timerSeconds > 0;
    var timerPos = S.timerPosition || 'below';
    // Same promise as the main offer: never sell her something she already has.
    // Found on the 16SEP2026 preview — the Pocket Square was in the cart from
    // the tie offer and the suit offer cheerfully added a second identical line.
    var addons = (rule.addons || []).filter(function (a) {
      return !(a.productId && cartHasProduct(cart, a.productId));
    });
    /* v9.9.13 — link mode. The button opens the product page instead of
     * adding, for products whose required options cannot be answered here. */
    var linkMode = rule.action === 'link' && offer.url;
    /* v9.9.19 — pick mode: choose several variants of ONE product in a single
     * go. Built for "pick 3 free swatches", so it is priced as free and capped
     * by the allowance worked out above, never by a hard-coded 3. */
    var pickMode = !linkMode && isPick;
    var multi = !linkMode && !pickMode && rule.layout === 'multi' && addons.length;
    /* v9.9.19 — quantity stepper, per offer, off unless the merchant turns it
     * on. Pocket squares are the case Jimmy asked for: people buy one per
     * groomsman, so making them re-run the whole flow six times was silly. */
    var qtyMode = !linkMode && !pickMode && !!rule.showQty;
    var maxQty = parseInt(rule.maxQty, 10);
    if (!maxQty || maxQty < 2) maxQty = 5;
    if (maxQty > 50) maxQty = 50;
    // v9.9.9: one colour shared by every add-on, defaulted to what she just added.
    var aoColour = multi ? defaultAoColour(addons, addedItem, variant) : '';

    var variantHtml = '';
    var avail = availableVariants(offer);

    /* ---- v9.9.19: pick-several markup ---- */
    var pickHtml = '';
    if (pickMode) {
      // Heading tells the truth about what is left, which is not always the
      // configured count — she may have spent some of it already.
      var pickHeadTpl = (allowance === (parseInt(rule.pickCount, 10) || 3))
        ? (S.pickHeading || 'Pick {n} free swatch{es}')
        : (S.pickRemaining || 'You have {n} free swatch{es} left');
      pickHtml =
        '<div class="kbup__opt kbup__pick">' +
          '<div class="kbup__optnow">' +
            '<span class="kbup__optlabel">' + esc(fill(pickHeadTpl, allowance)) + '</span>' +
          '</div>' +
          ddMultiHtml('kbup-pdd',
                      avail.map(function (v) { return { key: String(v.id), label: v.title, image: v.image || '' }; }),
                      S.searchPlaceholder || 'Search colors',
                      fill(S.pickPlaceholder || 'Choose up to {n}', allowance)) +
          '<div class="kbup__chips" id="kbup-chips"></div>' +
          '<div class="kbup__why" id="kbup-nudge"></div>' +
        '</div>';
    }

    /* ---- v9.9.19: quantity stepper ---- */
    var qtyHtml = '';
    if (qtyMode) {
      qtyHtml =
        '<div class="kbup__qty">' +
          '<span class="kbup__qtylabel" id="kbup-qtylabel">' + esc(S.qtyLabel || 'Quantity') + '</span>' +
          '<div class="kbup__qtyctl">' +
            '<button type="button" class="kbup__qtybtn" id="kbup-qtyminus" ' +
                    'aria-label="' + esc(S.qtyDownLabel || 'Decrease quantity') + '">&minus;</button>' +
            '<input class="kbup__qtyval" id="kbup-qtyval" type="text" inputmode="numeric" ' +
                   'value="1" aria-labelledby="kbup-qtylabel">' +
            '<button type="button" class="kbup__qtybtn" id="kbup-qtyplus" ' +
                    'aria-label="' + esc(S.qtyUpLabel || 'Increase quantity') + '">+</button>' +
          '</div>' +
        '</div>';
    }

    if (!linkMode && !pickMode && offer.variants.length > 1) {
      variantHtml =
        '<div class="kbup__opt">' +
          '<div class="kbup__optnow">' +
            '<span class="kbup__optlabel">' + esc(S.optionLabel || 'Color:') + '</span>' +
          '</div>' +
          (pick.matched
            ? '<div class="kbup__why" id="kbup-why">' + esc(S.matchedNote || 'Matched to what you just added') + '</div>'
            : '<div class="kbup__why" id="kbup-why"></div>') +
          ddHtml('kbup-mdd',
                 avail.map(function (v) { return { key: String(v.id), label: v.title, image: v.image || '' }; }),
                 String(variant.id),
                 S.searchPlaceholder || 'Search colors') +
        '</div>';
    }

    var addonHtml = '';
    if (multi) {
      /* v9.9.9 — one colour across all the add-ons.
       *
       * Before this, add-ons shipped a single fixed variant, so the tie and
       * pocket square arrived in whatever colour Shopify returned first
       * (measured: always Moss, whatever suit she was buying).
       *
       * A man buying a suit wants his tie and pocket square in the SAME
       * colour, so one picker drives all of them rather than one each —
       * which also keeps the card short on a 375px screen. Each add-on
       * resolves the chosen colour against its own variants and falls back
       * to its nearest match if it doesn't carry that exact one.
       */
      var aoRows = aoColourRows(addons);
      var aoPickerHtml = '';
      if (aoRows.length > 1) {
        aoPickerHtml =
          '<div class="kbup__opt kbup__aoopt">' +
            '<div class="kbup__optnow">' +
              '<span class="kbup__optlabel">' + esc(S.optionLabel || 'Color:') + '</span>' +
            '</div>' +
            ddHtml('kbup-aodd', aoRows, aoColour, S.searchPlaceholder || 'Search colors') +
          '</div>';
      }

      addonHtml = '<div class="kbup__addons"><div class="kbup__addonsh">' + esc(S.addonsHeading || 'Add these too') + '</div>' +
        aoPickerHtml +
        addons.map(function (a, i) {
          var rv = resolveAo(a, aoColour, rule);
          return '<label class="kbup__ao"><input type="checkbox" data-ao="' + i + '">' +
                 (rv.image
                   ? '<img class="kbup__aoth" data-aoth="' + i + '" src="' + esc(rv.image) + '" alt="" loading="lazy">'
                   : '<span class="kbup__aoth" data-aoth="' + i + '"></span>') +
                 '<span class="kbup__aon">' + esc(a.title) +
                   '<em class="kbup__aoc" data-aoname="' + i + '">' + (rv.title && rv.title !== 'Default Title' ? esc(rv.title) : '') + '</em>' +
                 '</span>' +
                 '<span class="kbup__aop" data-aoprice="' + i + '">' + (rv.price === 0 ? esc(S.freeLabel || 'Free') : money(rv.price)) + '</span></label>';
        }).join('') + '</div>';
    }

    var timerHtml = showTimer
      ? '<div class="kbup__timer" id="kbup-timer" aria-hidden="true">' + clockSvg() +
        '<span>' + esc(S.timerLabel || 'Offer held for') + '</span> <b id="kbup-clock"></b></div>'
      : '';

    root.innerHTML =
      '<div class="kbup__scrim" data-kbup-dismiss></div>' +
      '<div class="kbup__box">' +
        '<button type="button" class="kbup__x" id="kbup-x" aria-label="' + esc(S.closeLabel || 'Close') + '">&times;</button>' +
        '<div class="kbup__added">' +
          '<span class="kbup__tick" aria-hidden="true">&#10003;</span>' +
          '<span class="kbup__addedtxt"><b>' + esc(addedTitle) + '</b> ' + esc(S.addedSuffix || 'was added to your bag') + '</span>' +
          /* v9.9.13 — always render the bar slot when there is a timer.
             Mobile shows this one and hides the block timer, which is a whole
             row of the card reclaimed; desktop keeps whatever you chose. */
          (showTimer ? '<span class="kbup__bartimer" id="kbup-bartimer"></span>' : '') +
        '</div>' +
        '<div class="kbup__body">' +
          (showTimer && timerPos === 'above' ? timerHtml : '') +
          '<h2 class="kbup__head" id="kbup-head">' + esc(rule.headline) + '</h2>' +
          (rule.pitch ? '<p class="kbup__pitch">' + esc(rule.pitch) + '</p>' : '') +
          (showTimer && timerPos === 'below' ? timerHtml : '') +
          '<div class="kbup__offer">' +
            ((variant.imageLarge || variant.image || offer.image)
              ? '<img class="kbup__img" id="kbup-heroimg" src="' + esc(variant.imageLarge || variant.image || offer.image) + '" alt="' + esc(offer.title) + '" loading="lazy">'
              : '<span class="kbup__img" id="kbup-heroimg"></span>') +
            '<div class="kbup__info">' +
              '<div class="kbup__name">' + esc(offer.title) + '</div>' +
              (rule.meta ? '<div class="kbup__meta">' + esc(rule.meta) + '</div>' : '') +
              /* v9.9.19 — in pick mode the swatches really are free (the
                 "Free Swatches" automatic discount covers them), so show the
                 price as free and drop the was/save pair, which would only
                 add noise to a $0 line. */
              '<div class="kbup__prices">' +
                (!pickMode && variant.compare_at_price && variant.compare_at_price > variant.price
                  ? '<span class="kbup__was">' + money(variant.compare_at_price) + '</span>' : '') +
                '<span class="kbup__now' + (pickMode ? ' kbup__now--free' : '') + '" id="kbup-now">' +
                  (pickMode
                    ? esc(S.freeLabel || 'Free')
                    : (linkMode ? esc(S.fromLabel || 'From') + ' ' : '') + money(variant.price)) + '</span>' +
                (!pickMode && variant.compare_at_price && variant.compare_at_price > variant.price
                  ? '<span class="kbup__save">' + esc(S.saveLabel || 'Save') + ' ' + money(variant.compare_at_price - variant.price) + '</span>' : '') +
              '</div>' +
              variantHtml +
              qtyHtml +
            '</div>' +
          '</div>' +
          pickHtml +
          addonHtml +
          '<div class="kbup__actions">' +
            (linkMode
              ? '<a class="kbup__add" id="kbup-add" href="' + esc(offer.url) + '">' +
                  '<span id="kbup-addtxt">' + esc(rule.ctaLabel || 'Choose size') + '</span>' +
                '</a>'
              : '<button type="button" class="kbup__add" id="kbup-add">' +
                  '<span id="kbup-addtxt">' + esc(rule.ctaLabel || 'Add to my order') + '</span> ' +
                  '<span id="kbup-addprice"></span>' +
                '</button>') +
            '<button type="button" class="kbup__skip" id="kbup-skip">' + esc(rule.skipLabel || 'No thanks') + '</button>' +
          '</div>' +
        '</div>' +
      '</div>';

    /* ---- local state ---- */
    var chosen = variant;
    var picked = [];
    var expired = false;
    var qty = 1;                 // v9.9.19
    var pickSel = [];            // v9.9.19 — [{id,title,image}] in tap order
    var pickDd = null;           // v9.9.19 — set once ddMultiWire has run

    function totalCents() {
      if (pickMode) return 0;    // the automatic discount makes these free
      var t = chosen.price * qty;
      // v9.9.9: price comes from the variant she actually chose, not the
      // product's first variant. Colours can be priced differently.
      picked.forEach(function (i) { t += resolveAo(addons[i], aoColour, rule).price; });
      return t;
    }

    /* v9.9.19 — pick mode owns the CTA, the chips and the cap. */
    function syncPick() {
      // An expired offer must stay expired. Without this, removing a chip
      // after the countdown ran out would re-enable the Add button.
      if (expired) return;
      var n = pickSel.length;

      var box = root.querySelector('#kbup-chips');
      if (box) {
        box.innerHTML = pickSel.map(function (v, i) {
          return '<span class="kbup__chip">' +
            (v.image
              ? '<img class="kbup__chipsw" src="' + esc(v.image) + '" alt="" loading="lazy">'
              : '<span class="kbup__chipsw"></span>') +
            '<span class="kbup__chipn">' + esc(v.title) + '</span>' +
            '<button type="button" class="kbup__chipx" data-chip="' + i + '" ' +
                    'aria-label="' + esc((S.pickRemoveLabel || 'Remove') + ' ' + v.title) + '">&times;</button>' +
          '</span>';
        }).join('');
      }

      if (pickDd) {
        pickDd.setName(n
          ? fill(S.pickSelected || '{n} selected', n)
          : fill(S.pickPlaceholder || 'Choose up to {n}', allowance));
        pickDd.lock(n >= allowance);
      }

      var nudgeEl = root.querySelector('#kbup-nudge');
      if (nudgeEl) {
        nudgeEl.textContent = (n > 0 && n < allowance)
          ? fill(S.pickNudge || 'Add {n} more at no charge', allowance - n)
          : '';
      }

      // Partial picks are allowed on purpose: a customer who wants one colour
      // should not meet a dead button. The nudge above does the upselling.
      var addBtn = root.querySelector('#kbup-add');
      var txtEl = root.querySelector('#kbup-addtxt');
      if (addBtn) addBtn.disabled = (n === 0);
      if (txtEl) {
        txtEl.textContent = n === 0
          ? fill(S.pickEmptyCta || 'Pick your free swatch{es}', allowance)
          : fill(S.pickCta || 'Add {n} free swatch{es}', n);
      }
      var pEl = root.querySelector('#kbup-addprice');
      if (pEl) pEl.textContent = '';
    }

    function syncCta() {
      if (linkMode) return;                     // no price roll-up on a link
      if (pickMode) { syncPick(); return; }
      var n = qty + picked.length;
      var txt = n > 1 ? (S.ctaMulti || 'Add {n} to my order').replace('{n}', n) : (rule.ctaLabel || 'Add to my order');
      root.querySelector('#kbup-addtxt').textContent = txt;
      root.querySelector('#kbup-addprice').textContent = '· ' + money(totalCents());
    }
    syncCta();

    /* ---- variant picking: one dropdown, swatches visible (v9.9.10) ---- */
    ddWire(root, 'kbup-mdd', function (key) {
      var v = null;
      for (var i = 0; i < offer.variants.length; i++) {
        if (String(offer.variants[i].id) === String(key)) { v = offer.variants[i]; break; }
      }
      if (!v) return;
      chosen = v;
      // v9.9.11: the hero image must show the colour she picked, not the
      // product's default photo.
      // v9.9.13: the hero takes the 240px variant photo, not the 40px swatch.
      setThumb(root.querySelector('#kbup-heroimg'), v.imageLarge || v.image || offer.image, 'kbup__img', 'id', 'kbup-heroimg');
      var nowEl = root.querySelector('#kbup-now');
      if (nowEl) nowEl.textContent = money(v.price);
      var whyEl = root.querySelector('#kbup-why');
      if (whyEl) whyEl.textContent = S.yourPickNote || 'Your pick';
      syncCta();
    });

    /* ---- v9.9.19: pick several ---- */
    if (pickMode) {
      var pickIndexOf = function (id) {
        for (var i = 0; i < pickSel.length; i++) {
          if (String(pickSel[i].id) === String(id)) return i;
        }
        return -1;
      };

      pickDd = ddMultiWire(root, 'kbup-pdd', function (key, label, image) {
        if (expired) return;
        var at = pickIndexOf(key);
        if (at !== -1) {
          pickSel.splice(at, 1);
          pickDd.mark(key, false);
        } else {
          // Belt and braces: lock() already disables these rows, but a
          // keyboard or assistive-tech path could still get here.
          if (pickSel.length >= allowance) return;
          pickSel.push({ id: key, title: label, image: image || '' });
          pickDd.mark(key, true);
          // The hero follows the most recent choice so the photo always
          // matches something she just tapped.
          if (image) {
            setThumb(root.querySelector('#kbup-heroimg'), image, 'kbup__img', 'id', 'kbup-heroimg');
          }
        }
        syncPick();
      });

      var chipBox = root.querySelector('#kbup-chips');
      if (chipBox) {
        chipBox.addEventListener('click', function (e) {
          var b = e.target && e.target.closest ? e.target.closest('.kbup__chipx') : null;
          if (!b || expired) return;
          var i = parseInt(b.getAttribute('data-chip'), 10);
          if (isNaN(i) || !pickSel[i]) return;
          var gone = pickSel[i];
          pickSel.splice(i, 1);
          if (pickDd) pickDd.mark(gone.id, false);
          syncPick();
        });
      }

      syncPick();   // paints the empty state now that pickDd exists
    }

    /* ---- v9.9.19: quantity stepper ---- */
    if (qtyMode) {
      var qtyValEl = root.querySelector('#kbup-qtyval');
      var qtyMinusEl = root.querySelector('#kbup-qtyminus');
      var qtyPlusEl = root.querySelector('#kbup-qtyplus');

      var setQty = function (n) {
        n = parseInt(n, 10);
        if (isNaN(n) || n < 1) n = 1;
        if (n > maxQty) n = maxQty;
        qty = n;
        if (qtyValEl) qtyValEl.value = String(qty);
        // Disable at the ends rather than letting her tap into nothing.
        if (qtyMinusEl) qtyMinusEl.disabled = (qty <= 1);
        if (qtyPlusEl) qtyPlusEl.disabled = (qty >= maxQty);
        syncCta();
      };

      if (qtyMinusEl) qtyMinusEl.addEventListener('click', function () { setQty(qty - 1); });
      if (qtyPlusEl) qtyPlusEl.addEventListener('click', function () { setQty(qty + 1); });
      if (qtyValEl) {
        // change fires on Enter, blur catches a typed value she tabs away from.
        qtyValEl.addEventListener('change', function () { setQty(this.value); });
        qtyValEl.addEventListener('blur', function () { setQty(this.value); });
      }
      setQty(1);
    }

    /* ---- add-on color: one pick drives every add-on (v9.9.9) ---- */
    function syncAoColour() {
      for (var i = 0; i < addons.length; i++) {
        var rv = resolveAo(addons[i], aoColour, rule);
        var n = root.querySelector('[data-aoname="' + i + '"]');
        if (n) n.textContent = (rv.title && rv.title !== 'Default Title') ? rv.title : '';
        var pEl = root.querySelector('[data-aoprice="' + i + '"]');
        if (pEl) pEl.textContent = rv.price === 0 ? (S.freeLabel || 'Free') : money(rv.price);
        setThumb(root.querySelector('[data-aoth="' + i + '"]'), rv.image, 'kbup__aoth', 'data-aoth', i);
      }
      syncCta();
    }
    ddWire(root, 'kbup-aodd', function (key) {
      aoColour = key || aoColour;
      syncAoColour();
    });

    /* ---- add-ons ---- */
    [].forEach.call(root.querySelectorAll('[data-ao]'), function (cb) {
      cb.addEventListener('change', function () {
        var i = parseInt(cb.getAttribute('data-ao'), 10);
        if (cb.checked) { if (picked.indexOf(i) === -1) picked.push(i); }
        else { picked = picked.filter(function (x) { return x !== i; }); }
        syncCta();
      });
    });

    /* ---- timer: it really expires ----
     *
     * v9.9.16 — the countdown reads the CLOCK, it does not count ticks.
     *
     * It used to do `left--` once per setInterval, which assumes every tick
     * arrives. Browsers do not promise that: Chrome, Safari and every mobile
     * browser throttle or freeze timers in a background tab, so each skipped
     * tick was a second of lost time that never came back.
     *
     * Measured on the 18SEP2026 preview with the tab in the background:
     * 1753 seconds of real time elapsed and the card still read 2:02. That is
     * a scarcity timer telling the customer an offer is live 24 minutes after
     * it should have expired — and on a phone, switching apps mid-checkout is
     * the normal case, not the edge case.
     *
     * Against an absolute deadline a skipped tick costs nothing: the display
     * self-corrects the moment the tab wakes, and expiry fires on time.
     */
    if (showTimer) {
      var deadline = Date.now() + (rule.timerSeconds * 1000);
      var clockEl = root.querySelector('#kbup-clock');
      var barEl = root.querySelector('#kbup-bartimer');
      var wrapEl = root.querySelector('#kbup-timer');

      var secondsLeft = function () {
        return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      };
      var paint = function () {
        var left = secondsLeft();
        var m = Math.floor(left / 60), s = left % 60;
        var txt = m + ':' + (s < 10 ? '0' : '') + s;
        if (clockEl) clockEl.textContent = txt;
        if (barEl) barEl.innerHTML = clockSvg() + '<b>' + txt + '</b>';
      };
      paint();

      var onExpire = function () {
          expired = true;
          markExpired(rule.key);
          if (wrapEl) { wrapEl.classList.add('kbup__timer--done'); wrapEl.innerHTML = clockSvg() + '<span>' + esc(S.expiredLabel || 'This offer has expired') + '</span>'; }
          if (barEl) { barEl.classList.add('kbup__timer--done'); barEl.innerHTML = clockSvg() + '<b>' + esc(S.expiredShort || 'Expired') + '</b>'; }
          var addBtn = root.querySelector('#kbup-add');
          /* v9.9.13 — in link mode the CTA is an <a>, which cannot be disabled
             and carries no price element. Both were assumed here: the missing
             #kbup-addprice threw a TypeError that killed the rest of this
             callback, so the expiry never got tracked. */
          if (addBtn.tagName === 'BUTTON') {
            addBtn.disabled = true;
          } else {
            addBtn.setAttribute('aria-disabled', 'true');
            addBtn.classList.add('kbup__add--dead');
          }
          root.querySelector('#kbup-addtxt').textContent = S.expiredCta || 'Offer expired';
          var priceEl = root.querySelector('#kbup-addprice');
          if (priceEl) priceEl.textContent = '';
          track('upsell_expired', { offer: rule.key });
      };

      timerHandle = setInterval(function () {
        if (secondsLeft() <= 0) {
          clearInterval(timerHandle); timerHandle = null;
          onExpire();
        } else { paint(); }
      }, 1000);

      /* A throttled tab can be many seconds behind by the time it wakes.
         Re-read the clock the instant it becomes visible again rather than
         waiting up to a second for the next tick. */
      wakeHandler = function () {
        if (document.visibilityState !== 'visible' || expired) return;
        if (secondsLeft() <= 0) {
          if (timerHandle) { clearInterval(timerHandle); timerHandle = null; }
          onExpire();
        } else { paint(); }
      };
      document.addEventListener('visibilitychange', wakeHandler);
    }

    /* ---- actions ---- */
    function dismiss(reason) {
      track('upsell_dismissed', { offer: rule.key, reason: reason });
      finish(true);
    }
    root.querySelector('#kbup-x').addEventListener('click', function () { dismiss('close'); });
    root.querySelector('#kbup-skip').addEventListener('click', function () { dismiss('skip'); });
    var scrim = root.querySelector('[data-kbup-dismiss]');
    if (scrim && S.dismissOnScrim) scrim.addEventListener('click', function () { dismiss('scrim'); });

    root.querySelector('#kbup-add').addEventListener('click', function (ev) {
      if (linkMode) {
        // an expired link must not navigate either
        if (expired) { ev.preventDefault(); return; }
        // otherwise let the browser follow the href; record it and get out of the way
        track('upsell_clicked_through', { offer: rule.key, to: offer.url });
        return;
      }
      if (expired) return;
      // v9.9.19 — nothing ticked in pick mode means nothing to add.
      if (pickMode && !pickSel.length) return;
      var btn = this;
      btn.disabled = true;
      root.querySelector('#kbup-addtxt').textContent = S.addingLabel || 'Adding…';

      var items;
      if (pickMode) {
        /* v9.9.19 — one line per colour, quantity 1 each. Batching them into a
         * single /cart/add.js call matters: three separate POSTs would each
         * re-enter our own network observer and each fire the theme's cart
         * refresh. */
        items = pickSel.map(function (v) {
          return {
            id: v.id,
            quantity: 1,
            properties: { _upsell_source: 'popup', _upsell_offer: rule.key }
          };
        });
      } else {
        items = [{
          id: chosen.id,
          quantity: qty,
          properties: { _upsell_source: 'popup', _upsell_offer: rule.key }
        }];
        picked.forEach(function (i) {
          items.push({
            id: resolveAo(addons[i], aoColour, rule).id,
            quantity: 1,
            properties: { _upsell_source: 'popup-addon', _upsell_offer: rule.key }
          });
        });
      }

      addItems(items).then(function () {
        track('upsell_accepted', {
          offer: rule.key,
          variant: pickMode
            ? pickSel.map(function (v) { return v.title; }).join(', ')
            : chosen.title,
          quantity: pickMode ? pickSel.length : qty,
          value: totalCents() / 100,
          items: items.length
        });
        /* v9.9.12 — refetch the cart before the next offer.
         *
         * `cart` is the snapshot taken in evaluate(), BEFORE this add. Passing
         * it on meant offer 2 was filtered against a cart that did not contain
         * what she had just accepted — so ticking the Pocket Square on offer 1
         * and then seeing offer 2 offer the Pocket Square again. That is the
         * exact bug the dedupe was written to prevent. */
        getCart().then(function (fresh) {
          render(queue, index + 1, addedTitle, fresh || cart, addedItem);
        }, function () {
          render(queue, index + 1, addedTitle, cart, addedItem);
        });
      }, function (err) {
        log('add failed', err);
        // Fail visibly to us, silently to her: close and show the real cart.
        finish(true);
      });
    });

    track('upsell_shown', { offer: rule.key, position: index + 1 });

    // Focus management — the ADA demand means new UI cannot add surface area.
    state = state || {};
    state.lastFocus = document.activeElement;
    var firstBtn = root.querySelector('#kbup-add');
    if (firstBtn) { try { firstBtn.focus(); } catch (e) {} }
  }

  /* ------------------------------------------------------ keyboard + trap */

  document.addEventListener('keydown', function (e) {
    if (!root) return;
    if (e.key === 'Escape') { track('upsell_dismissed', { reason: 'esc' }); finish(true); return; }
    if (e.key !== 'Tab') return;
    var f = root.querySelectorAll('button:not([disabled]), input, [href], select, textarea');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* --------------------------------------------------------- orchestration */

  /* Consentmo's banner is z-index 2147483647 — max int — and on a 375px
   * screen it covers the whole offer card. Measured on the 16SEP2026 preview:
   * both the headline and the Add button returned covered=true, obscured by
   * CSM-COOKIE-CONSENT.csm-cmp-root.
   *
   * We do NOT raise our z-index above it. Hiding a consent banner on a store
   * with live CIPA exposure trades a small conversion gain for a real legal
   * one. The item is already safely in the cart, so the offer simply waits its
   * turn and appears once she has answered the banner. If she never answers,
   * we drop the offer rather than nag. */
  /* v9.9.4 — detect by OCCLUSION, not by guessing at markup.
   *
   * v9.9.3 used the selector 'csm-cmp-root', which is a TAG selector. The real
   * Consentmo element is <csm-cookie-consent class="csm-cmp-root">, so it never
   * matched and the wait never happened. Measured on the 16SEP2026 preview:
   *   querySelector('csm-cmp-root')  -> null
   *   querySelector('.csm-cmp-root') -> CSM-COOKIE-CONSENT, h=768, z=2147483647
   *
   * Height alone is also the wrong test: on desktop that same element is
   * full-height but only its bottom bar is opaque, so the offer is perfectly
   * visible and delaying it would cost sales for nothing.
   *
   * So we ask the only question that matters — is something covering the spot
   * where the offer card is about to appear? elementFromPoint at the centre of
   * the viewport answers it for any consent app, current or future, whatever
   * its markup. The selector list is only used to confirm the thing on top is
   * a consent banner and not, say, our own modal. */
  function blockingOverlay() {
    var sels = String(S.overlaySelectors || '.csm-cmp-root,csm-cookie-consent,#shopify-pc__banner,.shopify-pc__banner')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean);

    var cx = Math.round((window.innerWidth || 0) / 2);
    var cy = Math.round((window.innerHeight || 0) / 2);
    if (!cx || !cy) return null;

    var top;
    try { top = document.elementFromPoint(cx, cy); } catch (e) { return null; }
    if (!top) return null;

    for (var i = 0; i < sels.length; i++) {
      var el;
      try { el = document.querySelector(sels[i]); } catch (e) { continue; }
      if (!el) continue;
      if (el === top || el.contains(top)) return el;
    }
    return null;
  }

  function whenUnobstructed(cb) {
    if (!blockingOverlay()) { cb(); return; }
    var waited = 0;
    var limit = Math.max(0, parseInt(S.overlayWaitSeconds, 10) || 45) * 1000;
    log('offer deferred — consent banner is covering the screen');
    var iv = setInterval(function () {
      waited += 400;
      if (!blockingOverlay()) { clearInterval(iv); log('banner gone, showing offer'); cb(); return; }
      if (waited >= limit) { clearInterval(iv); log('banner never dismissed, dropping the offer'); }
    }, 400);
  }

  var evaluating = false;

  function evaluate(addedItem) {
    if (evaluating || root) return;
    evaluating = true;

    getCart().then(function (cart) {
      var queue = [];
      for (var i = 0; i < OFFERS.length && queue.length < MAX_CHAIN; i++) {
        var rule = OFFERS[i];
        if (wasSeen(rule.key)) continue;
        if (!ruleMatches(rule, addedItem)) continue;
        /* v9.9.19 — a multi-pick offer is gated on the free allowance, not on
         * "already in the cart". Owning one swatch must not cancel the two she
         * still has coming. Anything else keeps the original dedupe. */
        if (rule.layout === 'multiselect') {
          if (freeAllowance(rule, cart) <= 0) {
            log('skipping ' + rule.key + ' — no free allowance left in this cart');
            continue;
          }
        } else if (cartHasProduct(cart, rule.offer.productId)) {
          continue;
        }
        var pick = pickVariant(rule, rule.offer, addedItem);
        if (!pick) continue;                       // nothing in stock: render nothing
        queue.push({ rule: rule, offer: rule.offer, pick: pick });
      }
      evaluating = false;
      if (!queue.length) { log('no eligible offer'); releaseDrawer(true); return; }
      whenUnobstructed(function () {
        render(queue, 0, addedItem.product_title || addedItem.title || '', cart, addedItem);
      });
    }, function () { evaluating = false; releaseDrawer(true); });
  }

  /* --------------------------------------------- network observers (core) */

  function isAddUrl(url) {
    try { return /\/cart\/add(\.js)?(\?|$)/.test(String(url).split('#')[0]); }
    catch (e) { return false; }
  }

  function handleAddResponse(bodyText) {
    if (ourAddInFlight) return;               // our own add must not re-trigger
    var data = null;
    try { data = JSON.parse(bodyText); } catch (e) { data = null; }
    var item = null;
    if (data) {
      if (data.items && data.items.length) item = data.items[0];
      else if (data.id) item = data;
    }
    if (!item) {
      // Some paths POST a serialized form and get a non-JSON body back.
      // Fall back to the newest cart line.
      getCart().then(function (cart) {
        if (cart && cart.items && cart.items.length) evaluate(cart.items[cart.items.length - 1]);
        else releaseDrawer(true);
      }, function () { releaseDrawer(true); });
      return;
    }
    if (item.properties && item.properties._hidden_product) {
      releaseDrawer(true);                                           // rush charge line
      return;
    }
    // Cheap gate before the /cart.js round-trip. If nothing can match this
    // item, hand the drawer straight back instead of making her wait out a
    // fetch whose answer we already know.
    if (!anyRuleCouldMatch(item)) {
      log('nothing triggers on this item — releasing the drawer now');
      releaseDrawer(true);
      return;
    }
    evaluate(item);
  }

  // XHR — products-custom.js uses jQuery.ajax, which is XHR.
  (function () {
    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__kbup = (String(method || '').toUpperCase() === 'POST') && isAddUrl(url);
      return open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__kbup) {
        // Start holding the drawer NOW, before the theme's optimistic
        // cart_drawer_open() runs on the next line of products-custom.js.
        // Skipped entirely when this page cannot produce an offer, so a
        // no-offer add keeps the drawer it has always had, instantly.
        if (pageMightOffer()) holdDrawer(true);
        this.addEventListener('error', function () { releaseDrawer(true); });
        this.addEventListener('abort', function () { releaseDrawer(true); });
        this.addEventListener('load', function () {
          if (!(this.status >= 200 && this.status < 300)) releaseDrawer(true);
        });
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) {
            setTimeout(function (self) { handleAddResponse(self.responseText); }, 0, this);
          }
        });
      }
      return send.apply(this, arguments);
    };
  })();

  // fetch — Globo, Easy Bundle and anything modern.
  (function () {
    if (!window.fetch) return;
    var orig = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url);
      var method = (init && init.method) || (input && input.method) || 'GET';
      var watch = String(method).toUpperCase() === 'POST' && isAddUrl(url);
      if (watch && pageMightOffer()) holdDrawer(true);   // same reason as the XHR hook above
      var p = orig.apply(this, arguments);
      if (!watch) return p;
      return p.then(function (res) {
        if (res && res.ok) {
          try {
            res.clone().text().then(function (t) { setTimeout(function () { handleAddResponse(t); }, 0); });
          } catch (e) { releaseDrawer(true); }
        } else {
          releaseDrawer(true);
        }
        return res;
      }, function (err) { releaseDrawer(true); throw err; });
    };
  })();

  /* -------------------------------- retire the legacy recommendations popup */

  if (S.replaceLegacyPopup) {
    // sections/product-recommendations-popup.liquid is enabled on
    // product.bridesmaid-dress.json and present in all 41 product templates.
    // Removing the node BEFORE products-custom.js runs its DOM-ready handler
    // means its `if ($('.native-recommended-product-popup-section').length > 0)`
    // branch is skipped entirely, so it never adds .launch_popup to the ATC
    // button and the normal add-to-cart path runs. No templates/*.json edited.
    var kill = function () {
      var nodes = document.querySelectorAll('.native-recommended-product-popup-section, #shopify-section-product-recommendations-popup');
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
      }
      return nodes.length;
    };
    kill();
    document.addEventListener('DOMContentLoaded', kill);
  }

  log('ready', OFFERS.length, 'offer(s)');
})();
